/**
 * Fonctions de calcul pour les heures supplémentaires
 * @module lib/calculs-heures-sup
 */

import type {
  TypeHeureSup,
  TrancheHeureSup,
  HeuresSupSettings,
  ApercuCalculHeuresSup,
} from '@/types/heures-sup.types';

// =============================================================================
// Constantes
// =============================================================================

/**
 * Tranches standard du Code du Travail français
 */
export const TRANCHES_STANDARD: TrancheHeureSup[] = [
  { de: 36, a: 43, majoration: 25 },
  { de: 44, a: 999, majoration: 50 },
];

/**
 * Labels pour les types d'heures sup
 */
export const TYPE_HEURE_LABELS: Record<TypeHeureSup, string> = {
  standard: 'Standard',
  nuit: 'Heures de nuit',
  ferie: 'Dimanche/Férié',
};

// =============================================================================
// Fonctions de calcul
// =============================================================================

/**
 * Calcule le coût total des heures supplémentaires
 */
export function calculerCoutHeuresSup(
  nombreHeures: number,
  tauxHoraireBase: number,
  tauxMajoration: number
): number {
  const multiplicateur = 1 + tauxMajoration / 100;
  const tauxMajore = tauxHoraireBase * multiplicateur;
  return nombreHeures * tauxMajore;
}

/**
 * Calcule le taux majoré
 */
export function calculerTauxMajore(
  tauxHoraireBase: number,
  tauxMajoration: number
): number {
  return tauxHoraireBase * (1 + tauxMajoration / 100);
}

/**
 * Détermine la majoration applicable selon le type d'heure
 */
export function getMajorationPourType(
  typeHeure: TypeHeureSup,
  settings: HeuresSupSettings,
  trancheMajorationChoisie?: number
): number {
  switch (typeHeure) {
    case 'nuit':
      return settings.heures_sup_nuit_majoration;
    case 'ferie':
      return settings.heures_sup_ferie_majoration;
    case 'standard':
    default:
      // Si une tranche est choisie, l'utiliser
      if (trancheMajorationChoisie !== undefined) {
        return trancheMajorationChoisie;
      }
      // Sinon, prendre la première tranche par défaut
      const tranches = settings.heures_sup_tranches;
      return tranches.length > 0 ? tranches[0].majoration : 25;
  }
}

/**
 * Génère l'aperçu du calcul pour le formulaire
 */
export function genererApercuCalcul(
  nombreHeures: number,
  tauxHoraireBase: number,
  typeHeure: TypeHeureSup,
  settings: HeuresSupSettings,
  trancheMajorationChoisie?: number
): ApercuCalculHeuresSup {
  const tauxMajoration = getMajorationPourType(
    typeHeure,
    settings,
    trancheMajorationChoisie
  );
  const tauxMajore = calculerTauxMajore(tauxHoraireBase, tauxMajoration);
  const coutTotal = calculerCoutHeuresSup(
    nombreHeures,
    tauxHoraireBase,
    tauxMajoration
  );

  return {
    taux_horaire_base: tauxHoraireBase,
    taux_majoration: tauxMajoration,
    taux_majore: tauxMajore,
    cout_total: coutTotal,
  };
}

/**
 * Obtient les options de type d'heures sup disponibles selon les settings
 */
export function getTypesHeuresSupDisponibles(
  settings: HeuresSupSettings
): Array<{
  value: TypeHeureSup;
  label: string;
  majoration: number;
  disabled: boolean;
}> {
  const options: Array<{
    value: TypeHeureSup;
    label: string;
    majoration: number;
    disabled: boolean;
  }> = [];

  // Standard toujours disponible
  const premiereTranche = settings.heures_sup_tranches[0];
  options.push({
    value: 'standard',
    label: `Standard (+${premiereTranche?.majoration || 25}%)`,
    majoration: premiereTranche?.majoration || 25,
    disabled: false,
  });

  // Nuit si activé
  options.push({
    value: 'nuit',
    label: `Heures de nuit (+${settings.heures_sup_nuit_majoration}%)`,
    majoration: settings.heures_sup_nuit_majoration,
    disabled: !settings.heures_sup_nuit_actives,
  });

  // Férié si activé
  options.push({
    value: 'ferie',
    label: `Dimanche/Férié (+${settings.heures_sup_ferie_majoration}%)`,
    majoration: settings.heures_sup_ferie_majoration,
    disabled: !settings.heures_sup_ferie_actives,
  });

  return options;
}

/**
 * Obtient les tranches de majoration disponibles pour le type 'standard'
 */
export function getTranchesDisponibles(
  settings: HeuresSupSettings
): Array<{
  value: number;
  label: string;
  description: string;
}> {
  return settings.heures_sup_tranches.map((tranche) => ({
    value: tranche.majoration,
    label: `+${tranche.majoration}%`,
    description:
      tranche.a >= 999
        ? `À partir de la ${tranche.de}ème heure/semaine`
        : `De la ${tranche.de}ème à la ${tranche.a}ème heure/semaine`,
  }));
}

// =============================================================================
// Formatage
// =============================================================================

/**
 * Formate un nombre d'heures (ex: 2.5 → "2h30")
 */
export function formatHeures(heures: number): string {
  const heuresEntieres = Math.floor(heures);
  const minutes = Math.round((heures - heuresEntieres) * 60);
  if (minutes === 0) {
    return `${heuresEntieres}h00`;
  }
  return `${heuresEntieres}h${minutes.toString().padStart(2, '0')}`;
}

/**
 * Formate un coût en euros
 */
export function formatCoutHeuresSup(cout: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cout);
}

/**
 * Formate une majoration (ex: 25 → "+25%")
 */
export function formatMajoration(majoration: number): string {
  return `+${majoration}%`;
}

/**
 * Formate un taux horaire
 */
export function formatTauxHoraire(taux: number): string {
  return `${taux.toFixed(2)}€/h`;
}

// =============================================================================
// Validation
// =============================================================================

/**
 * Valide les données d'une entrée heures sup
 */
export function validerHeureSupData(data: {
  membre_id?: string;
  date_travail?: string;
  nombre_heures?: number;
  type_heure?: TypeHeureSup;
}): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!data.membre_id) {
    errors.push('Le membre est requis');
  }

  if (!data.date_travail) {
    errors.push('La date est requise');
  }

  if (!data.nombre_heures || data.nombre_heures <= 0) {
    errors.push('Le nombre d\'heures doit être supérieur à 0');
  }

  if (data.nombre_heures && data.nombre_heures > 24) {
    errors.push('Le nombre d\'heures ne peut pas dépasser 24h par jour');
  }

  if (!data.type_heure) {
    errors.push('Le type d\'heure est requis');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

// =============================================================================
// Sélecteurs d'heures prédéfinis
// =============================================================================

/**
 * Options de sélection rapide d'heures
 */
export const OPTIONS_HEURES: Array<{ value: number; label: string }> = [
  { value: 0.5, label: '0h30' },
  { value: 1, label: '1h00' },
  { value: 1.5, label: '1h30' },
  { value: 2, label: '2h00' },
  { value: 2.5, label: '2h30' },
  { value: 3, label: '3h00' },
  { value: 3.5, label: '3h30' },
  { value: 4, label: '4h00' },
  { value: 4.5, label: '4h30' },
  { value: 5, label: '5h00' },
  { value: 5.5, label: '5h30' },
  { value: 6, label: '6h00' },
];
